import { useState, useEffect, useRef, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";
import styles from "./PdfViewer.module.css";
import ZoomIcon from "../../assets/ph_caret-up-down-bold.png";
import RotateIcon from "../../assets/ic_outline-rotate-90-degrees-cw.png";
import DrawIcon from "../../assets/hugeicons_curvy-left-right-direction.png";
import UndoIcon from "../../assets/openmoji_left-arrow-curving-right.png";
import RedoIcon from "../../assets/openmoji_right-arrow-curving-left.png";
import DownloadIcon from "../../assets/iconoir_download.png";
import PrintIcon from "../../assets/uit_print.png";
import SendIcon from "../../assets/SendIcon.png";

// Set worker source using unpkg (has all npm versions)
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

interface Props {
    src: string;
    width?: number;
    height?: number | string;
    initialPage?: number;
}

export const PdfViewer = ({ src, width = 594, height = 774, initialPage }: Props) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const canvasRef2 = useRef<HTMLCanvasElement>(null);
    const drawCanvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const pagesContainerRef = useRef<HTMLDivElement>(null);
    const canvasContainerRef = useRef<HTMLDivElement>(null);
    const pagesEndRef = useRef<HTMLDivElement>(null);
    const pageCanvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
    const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [scale, setScale] = useState(1);
    const [rotation, setRotation] = useState<number>(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [thumbnails, setThumbnails] = useState<string[]>([]);
    const [fitMode, setFitMode] = useState<"width" | "page">("page"); // Start with fit to page
    const [drawMode, setDrawMode] = useState(false);
    const [drawHistory, setDrawHistory] = useState<ImageData[]>([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [menuOpen, setMenuOpen] = useState(false);
    const [twoPageView, setTwoPageView] = useState(false);
    const [showAnnotations, setShowAnnotations] = useState(true);
    const [showZoomIndicator, setShowZoomIndicator] = useState(false);
    const zoomTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isDrawingRef = useRef(false);
    const lastPosRef = useRef<{ x: number; y: number } | null>(null);
    const scrollRAFRef = useRef<number | null>(null);
    const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const renderTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const hasScrolledToInitialPage = useRef(false);

    // Load PDF document
    useEffect(() => {
        const loadPdf = async () => {
            try {
                setLoading(true);
                setError(null);
                hasScrolledToInitialPage.current = false; // Reset scroll flag for new PDF
                const loadingTask = pdfjsLib.getDocument(src);
                const pdf = await loadingTask.promise;
                setPdfDoc(pdf);
                setTotalPages(pdf.numPages);
                setCurrentPage(1);
            } catch (err) {
                console.error("Error loading PDF:", err);
                setError("Failed to load PDF");
            } finally {
                setLoading(false);
            }
        };

        if (src) {
            loadPdf();
        }

        return () => {
            if (pdfDoc) {
                pdfDoc.destroy();
            }
        };
    }, [src]);

    // Generate thumbnails for sidebar
    useEffect(() => {
        const generateThumbnails = async () => {
            if (!pdfDoc) return;
            const thumbs: string[] = [];
            for (let i = 1; i <= pdfDoc.numPages; i++) {
                const page = await pdfDoc.getPage(i);
                const viewport = page.getViewport({ scale: 0.2 });
                const canvas = document.createElement("canvas");
                const context = canvas.getContext("2d");
                if (!context) continue;
                canvas.height = viewport.height;
                canvas.width = viewport.width;
                await page.render({ canvasContext: context, viewport, canvas } as any).promise;
                thumbs.push(canvas.toDataURL());
            }
            setThumbnails(thumbs);
        };
        generateThumbnails();
    }, [pdfDoc]);

    // Track which pages have been rendered
    const renderedPagesRef = useRef<Set<number>>(new Set());

    // Render all pages with proper sizing
    const renderAllPages = useCallback(async () => {
        if (!pdfDoc || totalPages === 0) return;

        renderedPagesRef.current.clear();

        try {
            // Get first page to calculate base dimensions
            const firstPage = await pdfDoc.getPage(1);
            const baseViewport = firstPage.getViewport({ scale: 1, rotation });

            // Get actual container dimensions
            const container = canvasContainerRef.current;
            let containerWidth = container ? container.clientWidth - 32 : width - 32;
            const numericHeight = typeof height === "number" ? height : 774;
            let containerHeight = container ? container.clientHeight - 32 : numericHeight - 76;

            // Ensure valid dimensions
            if (containerWidth <= 0) containerWidth = width - 32;
            if (containerHeight <= 0) containerHeight = numericHeight - 76;

            // For two-page view, divide width by 2 (minus gap)
            let pageWidth = containerWidth;
            if (twoPageView) {
                pageWidth = (containerWidth - 16) / 2;
            }

            // Calculate scale to fit page in container
            const fitScaleWidth = pageWidth / baseViewport.width;
            const fitScaleHeight = containerHeight / baseViewport.height;

            // Always fit to container (100% zoom = page fits container)
            const baseScale = Math.min(fitScaleWidth, fitScaleHeight);

            // Apply user zoom
            const finalScale = baseScale * scale;

            // Render each page
            for (let i = 1; i <= totalPages; i++) {
                const canvas = pageCanvasRefs.current[i - 1];
                if (!canvas) continue;

                const page = await pdfDoc.getPage(i);
                const context = canvas.getContext("2d");
                if (!context) continue;

                const scaledViewport = page.getViewport({ scale: finalScale, rotation });

                canvas.width = scaledViewport.width;
                canvas.height = scaledViewport.height;

                await page.render({ canvasContext: context, viewport: scaledViewport, canvas } as any).promise;
                renderedPagesRef.current.add(i);
            }
        } catch (err) {
            console.error("Error rendering pages:", err);
        }
    }, [pdfDoc, totalPages, scale, rotation, twoPageView, width, height]);

    useEffect(() => {
        if (pdfDoc && totalPages > 0 && pageCanvasRefs.current.length !== totalPages) {
            pageCanvasRefs.current = new Array(totalPages).fill(null);
        }
    }, [pdfDoc, totalPages]);

    // Render pages when PDF loads or settings change
    useEffect(() => {
        if (!pdfDoc || totalPages === 0) return;

        let cancelled = false;
        const tryRender = (attempts: number) => {
            if (cancelled) return;
            // Check if canvas refs are populated
            const hasRefs = pageCanvasRefs.current.length === totalPages && pageCanvasRefs.current.some(c => c !== null);
            if (hasRefs) {
                renderAllPages();
            } else if (attempts < 20) {
                // Retry after a short delay (refs not yet assigned by React)
                setTimeout(() => tryRender(attempts + 1), 50);
            }
        };
        // Start after two rAF frames for initial layout
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                tryRender(0);
            });
        });

        return () => { cancelled = true; };
    }, [pdfDoc, totalPages, scale, rotation, sidebarOpen, twoPageView, renderAllPages]);

    // Scroll to initial page after PDF is loaded and rendered
    useEffect(() => {
        if (
            !hasScrolledToInitialPage.current &&
            initialPage &&
            initialPage > 0 &&
            initialPage <= totalPages &&
            totalPages > 0 &&
            !loading
        ) {
            // Wait for pages to be rendered before scrolling
            const scrollToInitialPage = () => {
                const targetCanvas = pageCanvasRefs.current[initialPage - 1];
                if (targetCanvas) {
                    targetCanvas.scrollIntoView({ behavior: "auto", block: "start" });
                    setCurrentPage(initialPage);
                    hasScrolledToInitialPage.current = true;
                }
            };

            // Use a small delay to ensure pages are rendered
            const timer = setTimeout(scrollToInitialPage, 300);
            return () => clearTimeout(timer);
        }
    }, [initialPage, totalPages, loading]);

    // Track current page based on scroll position
    useEffect(() => {
        const container = canvasContainerRef.current;
        if (!container || totalPages === 0) return;

        const handleScroll = () => {
            if (scrollRAFRef.current) return;

            scrollRAFRef.current = requestAnimationFrame(() => {
                const containerRect = container.getBoundingClientRect();
                const containerCenter = containerRect.top + containerRect.height / 2;

                let closestPage = 1;
                let closestDistance = Infinity;

                for (let i = 0; i < pageCanvasRefs.current.length; i++) {
                    const canvas = pageCanvasRefs.current[i];
                    if (!canvas) continue;

                    const rect = canvas.getBoundingClientRect();
                    const pageCenter = rect.top + rect.height / 2;
                    const distance = Math.abs(pageCenter - containerCenter);

                    if (distance < closestDistance) {
                        closestDistance = distance;
                        closestPage = i + 1;
                    }
                }

                setCurrentPage(closestPage);
                scrollRAFRef.current = null;
            });
        };

        container.addEventListener("scroll", handleScroll, { passive: true });
        return () => {
            container.removeEventListener("scroll", handleScroll);
            if (scrollRAFRef.current) {
                cancelAnimationFrame(scrollRAFRef.current);
            }
        };
    }, [totalPages]);

    // Handle Ctrl+scroll for zoom - MUST use native listener to prevent browser zoom
    useEffect(() => {
        const container = canvasContainerRef.current;
        if (!container || loading) return;

        const handleWheel = (e: WheelEvent) => {
            if (e.ctrlKey) {
                // CRITICAL: Prevent browser zoom
                e.preventDefault();
                e.stopPropagation();

                const delta = e.deltaY < 0 ? 0.15 : -0.15;

                setScale(prev => {
                    const newScale = Math.max(0.5, Math.min(3, prev + delta));
                    return Math.round(newScale * 100) / 100;
                });

                setShowZoomIndicator(true);

                if (zoomTimeoutRef.current) {
                    clearTimeout(zoomTimeoutRef.current);
                }

                zoomTimeoutRef.current = setTimeout(() => {
                    setShowZoomIndicator(false);
                }, 800);
            }
        };

        // passive: false is REQUIRED to call preventDefault on wheel events
        container.addEventListener("wheel", handleWheel, { passive: false });

        return () => {
            container.removeEventListener("wheel", handleWheel);
        };
    }, [loading, pdfDoc]);

    // Navigation handlers
    const goToPrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    // Zoom handlers
    const zoomIn = () => {
        setScale(prev => Math.min(prev + 0.25, 3));
    };

    const zoomOut = () => {
        setScale(prev => Math.max(prev - 0.25, 0.5));
    };

    const resetZoom = () => {
        setScale(1);
    };

    // Toggle between fit to width and fit to page
    const toggleFitMode = useCallback(() => {
        if (fitMode === "width") {
            setFitMode("page");
        } else {
            setFitMode("width");
        }
        setScale(1);
    }, [fitMode]);

    // Rotate handler (counterclockwise)
    const rotateCounterclockwise = () => {
        setRotation(prev => (prev - 90 + 360) % 360);
    };

    // Toggle draw mode
    const toggleDrawMode = () => {
        setDrawMode(prev => !prev);
    };

    // Save current canvas state to history
    const saveToHistory = useCallback(() => {
        if (!drawCanvasRef.current) return;
        const canvas = drawCanvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        // Remove any redo states after current index
        setDrawHistory(prev => {
            const newHistory = prev.slice(0, historyIndex + 1);
            newHistory.push(imageData);
            return newHistory;
        });
        setHistoryIndex(prev => prev + 1);
    }, [historyIndex]);

    // Native drawing event handlers for smooth performance
    useEffect(() => {
        const canvas = drawCanvasRef.current;
        if (!canvas) return;

        const getMousePos = (e: MouseEvent) => {
            const rect = canvas.getBoundingClientRect();
            const scaleX = canvas.width / rect.width;
            const scaleY = canvas.height / rect.height;
            return {
                x: (e.clientX - rect.left) * scaleX,
                y: (e.clientY - rect.top) * scaleY
            };
        };

        const handleMouseDown = (e: MouseEvent) => {
            if (!drawMode) return;
            e.preventDefault();
            const pos = getMousePos(e);
            const ctx = canvas.getContext("2d");
            if (!ctx) return;

            ctx.strokeStyle = "#FF0000";
            ctx.lineWidth = 2;
            ctx.lineCap = "round";
            ctx.lineJoin = "round";

            isDrawingRef.current = true;
            lastPosRef.current = pos;
        };

        const handleMouseMove = (e: MouseEvent) => {
            if (!isDrawingRef.current || !drawMode) return;
            e.preventDefault();
            const pos = getMousePos(e);
            const ctx = canvas.getContext("2d");
            if (!ctx || !lastPosRef.current) return;

            ctx.beginPath();
            ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
            ctx.lineTo(pos.x, pos.y);
            ctx.stroke();

            lastPosRef.current = pos;
        };

        const handleMouseUp = () => {
            if (isDrawingRef.current) {
                saveToHistory();
            }
            isDrawingRef.current = false;
            lastPosRef.current = null;
        };

        const handleMouseLeave = () => {
            if (isDrawingRef.current) {
                saveToHistory();
            }
            isDrawingRef.current = false;
            lastPosRef.current = null;
        };

        canvas.addEventListener("mousedown", handleMouseDown);
        canvas.addEventListener("mousemove", handleMouseMove);
        canvas.addEventListener("mouseup", handleMouseUp);
        canvas.addEventListener("mouseleave", handleMouseLeave);

        return () => {
            canvas.removeEventListener("mousedown", handleMouseDown);
            canvas.removeEventListener("mousemove", handleMouseMove);
            canvas.removeEventListener("mouseup", handleMouseUp);
            canvas.removeEventListener("mouseleave", handleMouseLeave);
        };
    }, [drawMode, saveToHistory, currentPage, showAnnotations]);

    // Undo drawing
    const undo = () => {
        if (historyIndex < 0 || !drawCanvasRef.current) return;
        const canvas = drawCanvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        if (historyIndex === 0) {
            // Clear canvas when undoing the first stroke
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            setHistoryIndex(-1);
        } else {
            // Restore previous state
            ctx.putImageData(drawHistory[historyIndex - 1], 0, 0);
            setHistoryIndex(historyIndex - 1);
        }
    };

    // Redo drawing
    const redo = () => {
        if (historyIndex >= drawHistory.length - 1 || !drawCanvasRef.current) return;
        const canvas = drawCanvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        const newIndex = historyIndex + 1;
        ctx.putImageData(drawHistory[newIndex], 0, 0);
        setHistoryIndex(newIndex);
    };

    // Store drawing as data URL for persistence across zoom
    const drawingDataRef = useRef<string | null>(null);

    // Save drawing to ref after each stroke
    const saveDrawingToRef = useCallback(() => {
        if (drawCanvasRef.current && drawCanvasRef.current.width > 0 && drawCanvasRef.current.height > 0) {
            drawingDataRef.current = drawCanvasRef.current.toDataURL();
        }
    }, []);

    // Update saveToHistory to also save to ref
    useEffect(() => {
        if (historyIndex >= 0) {
            saveDrawingToRef();
        }
    }, [historyIndex, saveDrawingToRef]);

    // Sync draw canvas size with PDF canvas
    useEffect(() => {
        const syncCanvas = () => {
            const pdfCanvas = pageCanvasRefs.current[currentPage - 1];
            if (pdfCanvas && drawCanvasRef.current) {
                const drawCanvas = drawCanvasRef.current;
                const ctx = drawCanvas.getContext("2d");
                const savedData = drawingDataRef.current;

                const newWidth = pdfCanvas.width;
                const newHeight = pdfCanvas.height;

                // Resize canvas (this clears it)
                drawCanvas.width = newWidth;
                drawCanvas.height = newHeight;

                // Restore drawing from saved data
                if (savedData && ctx) {
                    const img = new Image();
                    img.onload = () => {
                        ctx.drawImage(img, 0, 0, newWidth, newHeight);
                    };
                    img.src = savedData;
                }
            }
        };
        // Small delay to ensure PDF canvas has rendered first
        const timer = setTimeout(syncCanvas, 250);
        return () => clearTimeout(timer);
    }, [scale, rotation, currentPage]);

    // Clear drawings when page changes
    useEffect(() => {
        if (drawCanvasRef.current) {
            const ctx = drawCanvasRef.current.getContext("2d");
            if (ctx) {
                ctx.clearRect(0, 0, drawCanvasRef.current.width, drawCanvasRef.current.height);
            }
        }
        drawingDataRef.current = null;
        setDrawHistory([]);
        setHistoryIndex(-1);
    }, [currentPage]);

    // Open in new tab
    const openInNewTab = () => {
        window.open(src, "_blank");
    };

    // Toggle sidebar
    const toggleSidebar = () => {
        setSidebarOpen(prev => !prev);
    };

    // Toggle menu
    const toggleMenu = () => {
        setMenuOpen(prev => !prev);
    };

    // Menu options
    const toggleTwoPageView = () => {
        setTwoPageView(prev => !prev);
        setMenuOpen(false);
    };

    const toggleAnnotations = () => {
        setShowAnnotations(prev => !prev);
        setMenuOpen(false);
    };

    const presentDocument = () => {
        const elem = containerRef.current;
        if (elem?.requestFullscreen) {
            elem.requestFullscreen();
        }
        setMenuOpen(false);
    };

    const showDocumentProperties = () => {
        if (pdfDoc) {
            pdfDoc.getMetadata().then(metadata => {
                const info = metadata.info as any;
                alert(`Document Properties:\n\nPages: ${totalPages}\nTitle: ${info?.Title || 'N/A'}\nAuthor: ${info?.Author || 'N/A'}\nSubject: ${info?.Subject || 'N/A'}`);
            });
        }
        setMenuOpen(false);
    };

    const openInTabFromMenu = () => {
        window.open(src, "_blank");
        setMenuOpen(false);
    };

    // Print handler
    const handlePrint = () => {
        const printWindow = window.open(src, "_blank");
        if (printWindow) {
            printWindow.addEventListener("load", () => {
                printWindow.print();
            });
        }
    };

    // Download handler
    const handleDownload = () => {
        const link = document.createElement("a");
        link.href = src;
        link.download = src.split("/").pop() || "document.pdf";
        link.click();
    };

    // Scroll to bottom of PDF
    const scrollToBottom = () => {
        pagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // Use height prop for fixed container height - enforce strict bounds
    const containerStyle = { height, maxHeight: height, overflow: "hidden" as const };

    if (loading) {
        return (
            <div className={styles.container} style={containerStyle}>
                <div className={styles.loading}>Loading PDF...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.container} style={containerStyle}>
                <div className={styles.error}>{error}</div>
            </div>
        );
    }

    return (
        <div className={styles.container} style={containerStyle} ref={containerRef}>
            {/* Toolbar */}
            <div className={styles.toolbar}>
                <div className={styles.toolbarGroup}>
                    <button onClick={toggleSidebar} className={`${styles.toolbarButton} ${styles.sidebarToggle} ${sidebarOpen ? styles.active : ""}`} title="Toggle sidebar">
                        ☰
                    </button>
                    <span className={styles.pageInfo}>
                        {currentPage} / {totalPages}
                    </span>
                </div>
                <div className={styles.toolbarGroupRight}>
                    <button onClick={toggleFitMode} className={`${styles.toolbarButton} ${styles.fitButton}`} title={fitMode === "width" ? "Fit to page" : "Fit to width"}>
                        <img src={ZoomIcon} alt="Fit mode" className={`${styles.buttonIcon} ${fitMode === "page" ? styles.iconRotated : ""}`} />
                    </button>
                    <button onClick={rotateCounterclockwise} className={`${styles.toolbarButton} ${styles.fitButton}`} title="Rotate counterclockwise">
                        <img src={RotateIcon} alt="Rotate" className={styles.rotateIcon} />
                    </button>
                    <button onClick={toggleDrawMode} className={`${styles.toolbarButton} ${styles.fitButton} ${drawMode ? styles.active : ""}`} title="Draw on PDF">
                        <img src={DrawIcon} alt="Draw" className={styles.drawIcon} />
                    </button>
                    <button onClick={undo} disabled={historyIndex < 0} className={`${styles.toolbarButton} ${styles.fitButton}`} title="Undo">
                        <img src={UndoIcon} alt="Undo" className={styles.undoIcon} />
                    </button>
                    <button onClick={redo} disabled={historyIndex >= drawHistory.length - 1} className={`${styles.toolbarButton} ${styles.fitButton}`} title="Redo">
                        <img src={RedoIcon} alt="Redo" className={styles.redoIcon} />
                    </button>
                    <button onClick={handleDownload} className={`${styles.toolbarButton} ${styles.fitButton}`} title="Download">
                        <img src={DownloadIcon} alt="Download" className={styles.downloadIcon} />
                    </button>
                    <button onClick={handlePrint} className={`${styles.toolbarButton} ${styles.fitButton}`} title="Print">
                        <img src={PrintIcon} alt="Print" className={styles.printIcon} />
                    </button>
                    <div className={styles.menuContainer}>
                        <button onClick={toggleMenu} className={`${styles.toolbarButton} ${styles.fitButton} ${styles.menuButton}`} title="More options">
                            ⋮
                        </button>
                        {menuOpen && (
                            <div className={styles.dropdownMenu}>
                                <button onClick={toggleTwoPageView} className={styles.menuItem}>
                                    {twoPageView ? "✓ " : ""}Two Page View
                                </button>
                                <button onClick={toggleAnnotations} className={styles.menuItem}>
                                    {showAnnotations ? "✓ " : ""}Annotations
                                </button>
                                <button onClick={presentDocument} className={styles.menuItem}>
                                    Present
                                </button>
                                <button onClick={showDocumentProperties} className={styles.menuItem}>
                                    Document Properties
                                </button>
                                <div className={styles.menuDivider}></div>
                                <button onClick={openInTabFromMenu} className={styles.menuItem}>
                                    Open in Tab
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Content area with sidebar and canvas */}
            <div className={styles.contentArea}>
                {/* Sidebar with thumbnails */}
                {sidebarOpen && (
                    <div className={styles.sidebar}>
                        {thumbnails.map((thumb, index) => (
                            <div
                                key={index}
                                className={`${styles.thumbnailItem} ${currentPage === index + 1 ? styles.thumbnailActive : ""}`}
                                onClick={() => {
                                    setCurrentPage(index + 1);
                                    // Scroll to the page
                                    const pageCanvas = pageCanvasRefs.current[index];
                                    if (pageCanvas) {
                                        pageCanvas.scrollIntoView({ behavior: "smooth", block: "start" });
                                    }
                                }}
                            >
                                <img src={thumb} alt={`Page ${index + 1}`} className={styles.thumbnailImage} />
                                <span className={styles.thumbnailLabel}>{index + 1}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* PDF Canvas */}
                <div className={styles.canvasContainer} ref={canvasContainerRef}>
                    <div className={styles.pagesContainer} ref={pagesContainerRef}>
                        {twoPageView ? (
                            // Two-page view: render pages in pairs
                            Array.from({ length: Math.ceil(totalPages / 2) }, (_, pairIndex) => {
                                const leftPageIndex = pairIndex * 2;
                                const rightPageIndex = pairIndex * 2 + 1;
                                return (
                                    <div key={pairIndex} className={styles.twoPageWrapper}>
                                        <div className={styles.pageWrapper}>
                                            <canvas
                                                ref={el => { pageCanvasRefs.current[leftPageIndex] = el; }}
                                                className={styles.canvas}
                                            />
                                            <div className={styles.pageNumber}>{leftPageIndex + 1}</div>
                                        </div>
                                        {rightPageIndex < totalPages && (
                                            <div className={styles.pageWrapper}>
                                                <canvas
                                                    ref={el => { pageCanvasRefs.current[rightPageIndex] = el; }}
                                                    className={styles.canvas}
                                                />
                                                <div className={styles.pageNumber}>{rightPageIndex + 1}</div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            // Single page view
                            Array.from({ length: totalPages }, (_, i) => (
                                <div key={i} className={styles.pageWrapper}>
                                    <canvas
                                        ref={el => { pageCanvasRefs.current[i] = el; }}
                                        className={styles.canvas}
                                    />
                                    {showAnnotations && i === currentPage - 1 && (
                                        <canvas
                                            ref={drawCanvasRef}
                                            className={`${styles.drawCanvas} ${drawMode ? styles.drawCanvasActive : ""}`}
                                        />
                                    )}
                                    <div className={styles.pageNumber}>{i + 1}</div>
                                </div>
                            ))
                        )}
                        <div ref={pagesEndRef} />
                    </div>
                    {showZoomIndicator && (
                        <div className={styles.zoomIndicator}>
                            {Math.round(scale * 100)}%
                        </div>
                    )}
                </div>
                <button
                    onClick={scrollToBottom}
                    className={styles.scrollToBottomButton}
                    title="Scroll to bottom"
                    aria-label="Scroll to bottom"
                >
                    <img src={SendIcon} alt="Scroll to bottom" className={styles.scrollIcon} />
                </button>
            </div>
        </div>
    );
};
